import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { Color, Flex, Link } from '@brokerbay/elements-web';
import { Color as NewColor } from '@bb/elements-web-rebranding';
import { Feature } from '@bb/automaton';

import { buildGoogleMapsAddress, detectIOSDevice, featureEnabled } from 'utils';
import MobileMapsDrawer from '@app/external/MobileMapsDrawer';
import CoopCalendarSyncButton from '@app/business/ManageAppointmentPage/CoopCalendarSyncButton';
import { APPT_STATUSES_TO_INVITE, COLUMN_MARGIN_TOP } from '../../constants';
import InfoContainer from '../InfoContainer';
import ShowingDetailsContext from '../../ShowingDetailsV2/ShowingDetailsContext';

type AvailActionsColumnProps = {
  showFeedback: boolean;
  scrollToFeedback: () => void;
};

const AvailActionsColumn: React.FC<AvailActionsColumnProps> = ({
  showFeedback,
  scrollToFeedback,
}) => {
  const { appointment, listing, offerDetails, queryParams, hasFeature } =
    useContext(ShowingDetailsContext);
  const feedbackSubmitted = Boolean(appointment.feedbackCollected);
  const registerOfferUrl = `/#/listing/${listing.id}/offer/new?view=listing`;
  const isIOSDevice = detectIOSDevice();

  const [iosDrawerVisible, setIosDrawerVisible] = useState(false);
  const [destinationAddress, setDestinationAddress] = useState('');

  const hasSupraRebrandingFeature = featureEnabled('supraRebrandingFeature', 1);

  const clickDirections: React.MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    const destination = buildGoogleMapsAddress(listing as any);
    if (isIOSDevice) {
      setDestinationAddress(destination);
      setIosDrawerVisible(true);
    } else {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
        '_blank',
      );
    }
  };

  const feedbackStatusLabel = feedbackSubmitted
    ? 'Feedback submitted'
    : 'Feedback incomplete';

  const getFeedbackButton = () => (
    <StyledButton
      onClick={scrollToFeedback}
      $hasSupraRebrandingFeature={hasSupraRebrandingFeature}
    >
      {feedbackSubmitted ? 'View feedback' : 'Provide feedback'}
    </StyledButton>
  );

  return (
    <>
      <StyledColumnContainer vertical>
        {APPT_STATUSES_TO_INVITE.includes(appointment.status) &&
          !appointment.passed && (
            <>
              <InfoContainer
                icon="environmentTwoTone"
                colorName="primary"
                data-cy="showingDetails-getDirections-button"
                iconPaddingTop={2}
              >
                <StyledButton
                  onClick={clickDirections}
                  $hasSupraRebrandingFeature={hasSupraRebrandingFeature}
                >
                  Get directions
                </StyledButton>
              </InfoContainer>
              <InfoContainer
                icon="calendarTwoTone"
                colorName="primary"
                data-cy="showingDetails-calendarSync-button"
                iconPaddingTop={2}
              >
                <CoopCalendarSyncButton
                  appointment={appointment}
                  queryParams={queryParams}
                  newDetailsPageDesign
                />
              </InfoContainer>
            </>
          )}
        {hasFeature('OFFERS' as Feature) && offerDetails && (
          <InfoContainer
            icon="editTwoTone"
            colorName="primary"
            data-cy="showingDetails-registerOffers"
            iconPaddingTop={2}
          >
            <>
              {typeof offerDetails.offerCount === 'number' && (
                <StyledSpan>
                  {offerDetails.offerCount} offer
                  {offerDetails.offerCount !== 1 ? 's' : ''} registered <br />
                </StyledSpan>
              )}
              <StyledRegisterOfferLink
                size="large"
                href={registerOfferUrl}
                $hasSupraRebrandingFeature={hasSupraRebrandingFeature}
              >
                Register offer
              </StyledRegisterOfferLink>
            </>
          </InfoContainer>
        )}
        {showFeedback && (
          <InfoContainer
            icon="likeTwoTone"
            data-cy="showingDetails-Feedback"
            colorName="primary"
          >
            <>
              <StyledSpan>{feedbackStatusLabel}</StyledSpan>
              <br />
              {getFeedbackButton()}
            </>
          </InfoContainer>
        )}
      </StyledColumnContainer>
      <MobileMapsDrawer
        visible={iosDrawerVisible}
        setVisible={setIosDrawerVisible as any}
        destinationAddress={destinationAddress}
      />
    </>
  );
};

export default AvailActionsColumn;

const StyledColumnContainer = styled(Flex)`
  margin-top: ${COLUMN_MARGIN_TOP}px;
  & > [data-cy='showingDetails-calendarSync-button'] > * {
    font-size: 16px;
    button:hover {
      color: ${Color.HOVER_PRIMARY}!important;
    }
  }
  .calendar-sync-success > span {
    color: ${Color.BLACK};
    opacity: 0.88;
  }
`;
const StyledSpan = styled.span`
  color: ${Color.BLACK};
  opacity: 0.88;
`;
const StyledButton = styled.button<{ $hasSupraRebrandingFeature?: boolean }>`
  && {
    font-size: 16px;
    color: ${({ $hasSupraRebrandingFeature }) =>
      $hasSupraRebrandingFeature ? NewColor.PRIMARY : Color.PRIMARY};
    &:hover {
      color: ${({ $hasSupraRebrandingFeature }) =>
        $hasSupraRebrandingFeature
          ? NewColor.HOVER_PRIMARY
          : Color.HOVER_PRIMARY};
    }
  }
`;
const StyledRegisterOfferLink = styled(Link)<{
  $hasSupraRebrandingFeature?: boolean;
}>`
  && {
    color: ${({ $hasSupraRebrandingFeature }) =>
      $hasSupraRebrandingFeature ? NewColor.PRIMARY : Color.PRIMARY};
    &:hover {
      color: ${({ $hasSupraRebrandingFeature }) =>
        $hasSupraRebrandingFeature
          ? NewColor.HOVER_PRIMARY
          : Color.HOVER_PRIMARY};
    }
  }
`;
